import React, { useState } from 'react';
import { useAppContext, useAppActions } from '../../context/AppContext';
import useFacilityFeatures from '../../hooks/useFacilityFeatures';
import { 
  CreditCard, Plus, Search, Filter, Calendar, AlertTriangle, 
  CheckCircle, Clock, Eye, Edit3, UserCheck, DollarSign,
  TrendingUp, TrendingDown, FileText, Download, Printer,
  RefreshCw, Bell, Award, Star, Activity, Users,
  ChevronLeft, ChevronRight, MoreVertical, Receipt,
  Brain, Cpu, Sparkles, BarChart3, Zap, Globe, Target,
  Shield, TrendingUp as TrendingUpIcon, Tag, Building2
} from 'lucide-react';
import { billingInvoices, billingStats, paymentMethods, billingCategories } from '../../data/mockData';

const Billing = () => {
  const { state } = useAppContext();
  const actions = useAppActions();
  const { currentUser, deviceInfo } = state;
  const { optimization } = deviceInfo;
  
  // Get facility features for conditional UI
  const { isNDIS, isAgedCare, facilityConfig } = useFacilityFeatures();

  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // AI Billing Optimization Data
  const aiBillingData = {
    optimization: {
      paymentRate: 92.3,
      collectionEfficiency: 88.7,
      costReduction: 15.2,
      revenueGrowth: 12.5
    },
    predictions: {
      paymentDelays: 3,
      revenueOpportunities: 5,
      costSavings: 8.5,
      riskInvoices: 2
    },
    recommendations: [
      {
        id: 'BILL_AI001',
        type: 'Payment',
        priority: 'High',
        title: 'Implement automated payment reminders',
        description: 'AI detected 15% improvement potential in payment collection with automated reminders.',
        impact: 'Increase payment rate by 8%',
        invoices: ['INV-2024-001', 'INV-2024-003'],
        status: 'Pending',
        aiConfidence: 94
      },
      {
        id: 'BILL_AI002',
        type: 'Cost',
        priority: 'Medium',
        title: 'Optimize billing categories for better tracking',
        description: 'AI analysis shows 20% cost reduction potential through category optimization.',
        impact: 'Reduce operational costs by $2.5K monthly',
        invoices: ['All Categories'],
        status: 'Recommended',
        aiConfidence: 87
      },
      {
        id: 'BILL_AI003',
        type: 'Revenue',
        priority: 'Low',
        title: 'Implement dynamic pricing for premium services',
        description: 'AI suggests implementing dynamic pricing for high-demand services.',
        impact: 'Increase revenue by 12%',
        invoices: ['Premium Services'],
        status: 'Suggested',
        aiConfidence: 79
      }
    ],
    analytics: {
      paymentTrends: {
        improving: 8,
        stable: 12,
        declining: 2
      },
      revenueSources: {
        residential: 65,
        respite: 20,
        palliative: 10,
        other: 5
      },
      costOptimization: {
        excellent: 6,
        good: 8,
        needsImprovement: 3
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'bg-green-100 text-green-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentMethodColor = (method) => {
    switch (method) {
      case 'Direct Debit': return 'bg-blue-100 text-blue-800';
      case 'Credit Card': return 'bg-purple-100 text-purple-800';
      case 'Bank Transfer': return 'bg-green-100 text-green-800';
      case 'Cash': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredInvoices = billingInvoices.filter(invoice => {
    const matchesSearch = invoice.residentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         invoice.facility.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         invoice.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || invoice.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl p-6 text-white shadow-xl min-h-[120px] flex items-center">
        <div className="flex justify-between items-center w-full">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              {isNDIS ? 'NDIS Claims & Budget Management' : isAgedCare ? 'Aged Care Billing & Funding' : 'Billing Management'}
              <CreditCard className="w-8 h-8 text-amber-300 animate-pulse" />
            </h1>
            <p className="text-base mt-2 opacity-90">
              {isNDIS 
                ? 'Manage NDIS plan budgets, claims, and participant funding' 
                : isAgedCare 
                ? 'Manage AN-ACC funding, residential care billing, and Support at Home' 
                : 'Manage invoices, payments, and financial records'}
            </p>
            {facilityConfig && (
              <div className="mt-2 flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  isNDIS ? 'bg-green-200 text-green-800' : 
                  isAgedCare ? 'bg-purple-200 text-purple-800' : 
                  'bg-gray-200 text-gray-800'
                }`}>
                  <Tag className="w-3 h-3 inline mr-1" />
                  {isNDIS ? 'NDIS' : isAgedCare ? 'Aged Care' : 'General'}
                </span>
              </div>
            )}
          </div>
          <button 
            onClick={() => {
              console.log('Create new invoice');
            }}
            className={`bg-white text-amber-600 ${optimization.buttonSize} rounded-xl font-semibold hover:bg-opacity-90 ${optimization.animationSpeed} flex items-center gap-2`}
          >
            <Plus className={`${optimization.iconSize}`} />
            New Invoice
          </button>
        </div>
      </div>

      {/* Financial Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{formatCurrency(billingStats.monthlyRevenue)}</h3>
              <p className="text-sm text-gray-600">Monthly Revenue</p>
              <div className="flex items-center gap-1 text-xs">
                <TrendingUp className="w-3 h-3 text-green-600" />
                <span className="text-green-600">+12.5%</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{billingStats.paidInvoices}</h3>
              <p className="text-sm text-gray-600">Paid Invoices</p>
              <div className="flex items-center gap-1 text-xs">
                <TrendingUp className="w-3 h-3 text-green-600" />
                <span className="text-green-600">+8.2%</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{billingStats.overdueInvoices}</h3>
              <p className="text-sm text-gray-600">Overdue</p>
              <div className="flex items-center gap-1 text-xs">
                <TrendingDown className="w-3 h-3 text-green-600" />
                <span className="text-green-600">-15.3%</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Receipt className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{billingStats.paymentRate}%</h3>
              <p className="text-sm text-gray-600">Payment Rate</p>
              <div className="flex items-center gap-1 text-xs">
                <TrendingUp className="w-3 h-3 text-green-600" />
                <span className="text-green-600">+2.1%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview', icon: CreditCard },
              { id: 'invoices', label: 'Invoices', icon: FileText },
              { id: 'payments', label: 'Payments', icon: Receipt },
              { id: 'analytics', label: 'Analytics', icon: TrendingUp },
              { id: 'ai', label: 'AI Insights', icon: Brain }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-amber-500 text-amber-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search invoices, residents, facilities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="Paid">Paid</option>
                <option value="Pending">Pending</option>
                <option value="Overdue">Overdue</option>
              </select>
              <button 
                onClick={() => {
                  console.log('Open date range picker');
                }}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Calendar className="w-4 h-4" />
                Date Range
              </button>
            </div>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl">
                  <h3 className="font-semibold text-blue-800 mb-4">Recent Invoices</h3>
                  <div className="space-y-3">
                    {billingInvoices.slice(0, 3).map((invoice) => (
                      <div key={invoice.id} className="bg-white p-3 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-medium text-sm">{invoice.residentName}</h4>
                            <p className="text-xs text-gray-600">{invoice.facility}</p>
                          </div>
                          <div className="text-right">
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(invoice.status)}`}>
                              {invoice.status}
                            </span>
                            <div className="text-xs font-medium mt-1">{formatCurrency(invoice.amount)}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl">
                  <h3 className="font-semibold text-green-800 mb-4">Payment Methods</h3>
                  <div className="space-y-3">
                    {paymentMethods.map((method) => (
                      <div key={method.id} className="bg-white p-3 rounded-lg">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs rounded-full ${getPaymentMethodColor(method.name)}`}>
                              {method.name}
                            </span>
                          </div>
                          <span className="text-sm font-medium">{method.percentage}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'invoices' && (
            <div className="space-y-4">
              {filteredInvoices.map((invoice) => (
                <div key={invoice.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold">{invoice.residentName}</h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(invoice.status)}`}>
                          {invoice.status}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-2">
                        {invoice.facility} • Invoice #{invoice.id} • {formatCurrency(invoice.amount)}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {invoice.invoiceDate}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Due: {invoice.dueDate}
                        </span>
                        {invoice.paymentMethod && (
                          <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${getPaymentMethodColor(invoice.paymentMethod)}`}>
                            <CreditCard className="w-3 h-3" />
                            {invoice.paymentMethod}
                          </span>
                        )}
                      </div>
                      {invoice.items && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="text-xs text-gray-600 mb-2">Invoice Items:</div>
                          <div className="space-y-1">
                            {invoice.items.map((item, index) => (
                              <div key={index} className="flex justify-between text-xs">
                                <span>{item.description}</span>
                                <span className="font-medium">{formatCurrency(item.amount)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => {
                          console.log('View invoice details');
                        }}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => {
                          console.log('Edit invoice');
                        }}
                        className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => {
                          console.log('Download invoice');
                        }}
                        className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="font-semibold text-lg mb-4">Payment Methods Distribution</h3>
                  <div className="space-y-3">
                    {paymentMethods.map((method) => (
                      <div key={method.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${getPaymentMethodColor(method.name)}`}>
                            {method.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-amber-600 h-2 rounded-full" 
                              style={{ width: `${method.percentage}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium">{method.percentage}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="font-semibold text-lg mb-4">Revenue by Category</h3>
                  <div className="space-y-3">
                    {billingCategories.map((category) => (
                      <div key={category.name} className="flex items-center justify-between">
                        <span className="text-sm">{category.name}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-600 h-2 rounded-full" 
                              style={{ width: `${category.percentage}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium">{formatCurrency(category.amount)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">Average Invoice</h3>
                    <DollarSign className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="text-2xl font-bold text-amber-600">{formatCurrency(billingStats.averageInvoiceAmount)}</div>
                  <div className="flex items-center gap-1 text-sm">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <span className="text-green-600">+5.2%</span>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">Payment Rate</h3>
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="text-2xl font-bold text-green-600">{billingStats.paymentRate}%</div>
                  <div className="flex items-center gap-1 text-sm">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <span className="text-green-600">+2.1%</span>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">Overdue Rate</h3>
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="text-2xl font-bold text-red-600">7.7%</div>
                  <div className="flex items-center gap-1 text-sm">
                    <TrendingDown className="w-4 h-4 text-green-600" />
                    <span className="text-green-600">-1.5%</span>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">Monthly Growth</h3>
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="text-2xl font-bold text-blue-600">+12.5%</div>
                  <div className="flex items-center gap-1 text-sm">
                    <span className="text-gray-600">vs last month</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ai' && (
            <div className="space-y-6">
              {/* AI Billing Optimization Dashboard */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-100 p-8 rounded-2xl border border-purple-200">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-purple-800 mb-2 flex items-center gap-3">
                      <Brain className="w-6 h-6 text-purple-600" />
                      AI-Powered Billing Optimization
                    </h3>
                    <p className="text-purple-600">Intelligent billing management and cost optimization</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-green-600 font-medium">AI Active</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                        <CheckCircle className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-green-600">{aiBillingData.optimization.paymentRate}%</div>
                        <div className="text-sm text-gray-600">Payment Rate</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Target</span>
                        <span className="text-green-600">90%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full" style={{width: `${aiBillingData.optimization.paymentRate}%`}}></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                        <TrendingUp className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-blue-600">{aiBillingData.optimization.collectionEfficiency}%</div>
                        <div className="text-sm text-gray-600">Collection Efficiency</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Target</span>
                        <span className="text-blue-600">85%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full" style={{width: `${aiBillingData.optimization.collectionEfficiency}%`}}></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
                        <DollarSign className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-purple-600">{aiBillingData.optimization.costReduction}%</div>
                        <div className="text-sm text-gray-600">Cost Reduction</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Savings</span>
                        <span className="text-purple-600">$2.5K</span>
                      </div>
                      <div className="text-xs text-gray-500">Monthly</div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                        <Zap className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-green-600">+{aiBillingData.optimization.revenueGrowth}%</div>
                        <div className="text-sm text-gray-600">Revenue Growth</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>This Month</span>
                        <span className="text-green-600">+{aiBillingData.optimization.revenueGrowth}%</span>
                      </div>
                      <div className="text-xs text-gray-500">AI-driven</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Recommendations */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-800">AI Billing Recommendations</h4>
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">
                      {aiBillingData.recommendations.length} Active
                    </span>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  {aiBillingData.recommendations.map((recommendation) => (
                    <div key={recommendation.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h5 className="font-semibold text-sm">{recommendation.title}</h5>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              recommendation.priority === 'High' ? 'bg-red-100 text-red-800' :
                              recommendation.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {recommendation.priority}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mb-2">{recommendation.description}</p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              {recommendation.impact}
                            </span>
                            <span className="flex items-center gap-1">
                              <Brain className="w-3 h-3" />
                              {recommendation.aiConfidence}% confidence
                            </span>
                          </div>
                          <div className="mt-2">
                            <span className="text-xs text-blue-600">Invoices: {recommendation.invoices.join(', ')}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg">
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Billing Analytics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <div className="p-6 border-b border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-800">Payment Trends</h4>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium">Improving</span>
                      </div>
                      <span className="text-sm text-green-600 font-medium">{aiBillingData.analytics.paymentTrends.improving} invoices</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span className="text-sm font-medium">Stable</span>
                      </div>
                      <span className="text-sm text-blue-600 font-medium">{aiBillingData.analytics.paymentTrends.stable} invoices</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        <span className="text-sm font-medium">Declining</span>
                      </div>
                      <span className="text-sm text-red-600 font-medium">{aiBillingData.analytics.paymentTrends.declining} invoices</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <div className="p-6 border-b border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-800">Revenue Sources</h4>
                  </div>
                  <div className="p-6 space-y-4">
                    {Object.entries(aiBillingData.analytics.revenueSources).map(([source, percentage]) => (
                      <div key={source} className="flex items-center justify-between">
                        <span className="text-sm font-medium capitalize">{source}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full" 
                              style={{width: `${percentage}%`}}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-600 w-8">{percentage}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Predictive Insights */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="p-6 border-b border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-800">Predictive Insights</h4>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{aiBillingData.predictions.paymentDelays}</div>
                      <div className="text-sm text-gray-600 mb-1">Payment Delays</div>
                      <div className="text-xs text-gray-500">Expected delays</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{aiBillingData.predictions.revenueOpportunities}</div>
                      <div className="text-sm text-gray-600 mb-1">Revenue Opportunities</div>
                      <div className="text-xs text-gray-500">Potential growth</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">${aiBillingData.predictions.costSavings}K</div>
                      <div className="text-sm text-gray-600 mb-1">Cost Savings</div>
                      <div className="text-xs text-gray-500">Monthly potential</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">{aiBillingData.predictions.riskInvoices}</div>
                      <div className="text-sm text-gray-600 mb-1">Risk Invoices</div>
                      <div className="text-xs text-gray-500">Require attention</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Plus className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold">New Invoice</h3>
              <p className="text-sm text-gray-600">Create invoice</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Receipt className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold">Payment History</h3>
              <p className="text-sm text-gray-600">View payments</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Bell className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold">Alerts</h3>
              <p className="text-sm text-gray-600">View notifications</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Billing;
